import React, { type PropsWithChildren, useContext } from 'react';

import { Box, type ComponentImage, LayoutContext, NavSection } from '@lucid/components';
import { graphql, useStaticQuery } from 'gatsby';
import { useNavMenu, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from 'types/types';

interface LearningCampusNavProps {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  languageKey: Lucid.I18n.LanguagesShortCode;
  logo: ComponentImage;
  product?: Lucid.Site.Product | 'Lucid';
}

const LearningCampusNav: React.FC<PropsWithChildren<LearningCampusNavProps>> = ({
  buttonAriaLabel,
  languageKey,
  logo,
  product,
}) => {
  const navigationItems = useNavMenu(2, languageKey);
  const {
    directus: { node },
  } = useStaticQuery(graphql`
    {
      directus {
        node: learning_campus_hub_by_id(id: "1") {
          translations {
            languages_code {
              short_code
            }
            path {
              url
            }
          }
        }
      }
    }
  `);

  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_learning_campus_go_to_lucid'],
    shortCode,
  });
  const translations = node?.translations as Lucid.Directus.Generated.Learning_Campus_Hub_Translations[];

  const navButtons = [
    {
      display_as_button: true,
      default_link_text: withSmartling('mktg_learning_campus_go_to_lucid'),
      final_url: 'https://lucid.app/documents',
      beacon_id: 'go-to-lucid',
      id: 'go-to-lucid',
    },
  ] as Lucid.Directus.Generated.Menu_Link[];

  if (product) {
    const skilljarLink = navigationItems?.find(item => item.id === '18')?.link;
    let urlSuffix = '';

    switch (product) {
      case 'chart':
        urlSuffix = 'lucidchart';
        break;
      case 'spark':
        urlSuffix = 'lucidspark';
        break;
      default:
        urlSuffix = 'lucid-training-labs';
    }

    if (skilljarLink?.final_url && product !== 'co') {
      skilljarLink.final_url = `${skilljarLink?.final_url.replace(/\/$/, '')}/page/${urlSuffix}`;
    }
  }

  const mainLink = translations?.find(tr => tr.languages_code?.short_code == languageKey);

  return (
    <Box
      css={theme => ({
        'ul:nth-of-type(1)': {
          li: {
            margin: `${theme.space.s0} ${theme.space.s16}px`,
            a: {
              position: 'relative',
              '&:hover': {
                color: 'inherit',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  borderBottom: `5px solid ${theme.colors.coolGrey[100]}`,
                },
              },
            },
          },
        },
      })}
    >
      <NavSection
        buttonAriaLabel={buttonAriaLabel}
        siteNav={{
          items: navigationItems || [],
          buttons: navButtons,
          logo,
          logoLink:
            product && `${product}` !== 'co'
              ? `${mainLink?.path?.url}/lucid${product.toLowerCase().replace('lucid', '')}`
              : mainLink?.path?.url,
        }}
        variants={{ logo: 'learningCampus' }}
      />
    </Box>
  );
};

export default LearningCampusNav;
